import {
  createResource,
  RouterContext,
  type Query,
} from 'react-resource-router';

import { StatsigFeatureKeys } from 'src/features';
import { ResourceContext } from 'src/router/types';

import { SearchResponse } from './api-types';
import {
  WorkspaceSearchPageViewModel,
  WorkspaceSearchPageFileViewModel,
} from './types/api';
import { fetchWorkspaceSearchResults } from './utils/fetch-search-results';

type ParsedWorkspaceSearchQuery = {
  q?: string;
};

export const queryToKey = (query: Query): ParsedWorkspaceSearchQuery => {
  return {
    q: query.q || undefined,
  };
};

export const workspaceSearchResource = createResource({
  type: 'WORKSPACE_SEARCH',
  getKey: ({ match, query }: RouterContext) => {
    const parsedQueryString = new URLSearchParams(queryToKey(query)).toString();
    return `workspace:${match.params.workspaceSlug}::query:${parsedQueryString}`;
  },
  maxAge: 5 * 60 * 1000, // 5 minutes
  getData: async (
    { match, query },
    { csrftoken, appData }: ResourceContext
  ) => {
    if (!appData.features[StatsigFeatureKeys.isNewCodeSearchEnabled]) {
      return undefined;
    }

    const { workspaceSlug } = match.params;

    /**
     * If we can't match the URL to a workspace slug, this resource is being used on a route
     * that it shouldn't be used with
     */
    if (!workspaceSlug) {
      return undefined;
    }

    const searchQuery = query.q;

    if (!searchQuery) {
      return undefined;
    }

    const response = await fetchWorkspaceSearchResults(
      { workspaceSlug, searchQuery },
      csrftoken
    );

    return convert(response);
  },
});

export function convert(
  searchResponse: SearchResponse
): WorkspaceSearchPageViewModel {
  const repoIdToName = new Map(
    searchResponse.repositories.map(repo => {
      return [repo.id, repo.name];
    })
  );

  const rankOrderedFiles: WorkspaceSearchPageFileViewModel[] = [];

  const filesByRepoId = new Map<string, WorkspaceSearchPageFileViewModel[]>();
  for (const file of searchResponse.matched_files) {
    const fileViewModel: WorkspaceSearchPageFileViewModel = {
      id: file.id,
      language: file.language,
      name: file.name,
      repositoryName: repoIdToName.get(file.repositoryId) ?? '',
      path: file.path,
      chunks: file.matched_chunks.map(chunk => ({
        id: chunk.id,
        sourceCode: chunk.source_code,
        startLine: chunk.line_range.start,
        matchedRanges: chunk.matched_ranges,
      })),
      url: file.url,
    };

    const existing = filesByRepoId.get(file.repositoryId);
    if (existing) {
      existing.push(fileViewModel);
    } else {
      filesByRepoId.set(file.repositoryId, [fileViewModel]);
    }
    rankOrderedFiles.push(fileViewModel);
  }

  const repositories = searchResponse.repositories.map(repository => {
    const files = filesByRepoId.get(repository.id) ?? [];

    return {
      id: repository.id,
      name: repository.name,
      files,
      workspaceName: repository.ns,
    };
  });

  return {
    nextCursor: searchResponse.pagination?.next_cursor,
    repositories,
    searchRequestId: searchResponse.id,
    totalCount: searchResponse.metadata?.total_count || 0,
    rankOrderedFiles,
  };
}
